const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;

/** Parse tracker notes: `C-2`, `C#3`, `Db4` (dash is a separator; octave is the number). */
export function parseNoteName(token: string): number | undefined {
  const raw = token.trim();
  if (!raw || raw === "...." || raw === "..." || raw === ".." || raw === ".") {
    return undefined;
  }
  if (raw === "---" || raw === "===" || raw.toUpperCase() === "OFF") {
    return undefined; // caller treats as note-off via isNoteOffToken
  }

  const match = /^([A-Ga-g])([#b]?)[-]?(-?\d+)$/.exec(raw);
  if (!match) {
    return undefined;
  }
  const letter = match[1]!.toUpperCase();
  const accidental = match[2] ?? "";
  const octave = Number(match[3]);
  const natural = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }[letter];
  if (natural === undefined || !Number.isFinite(octave)) {
    return undefined;
  }
  let semitone = natural;
  if (accidental === "#") {
    semitone += 1;
  } else if (accidental === "b") {
    semitone -= 1;
  }
  // MIDI: C4 = 60 → C-1 = 0, C-2 = 12, …
  return (octave + 1) * 12 + semitone;
}

export function isNoteOffToken(token: string): boolean {
  const raw = token.trim().replace(/\.+$/g, "").trim().toUpperCase();
  return raw === "---" || raw === "===" || raw === "OFF";
}

export function isEmptyNoteToken(token: string): boolean {
  const raw = token.trim();
  return raw === "" || raw === "." || raw === ".." || raw === "..." || raw === "....";
}

/** Format MIDI as tracker-style `C-2` / `C#3`. */
export function formatNoteName(midi: number): string {
  const note = ((midi % 12) + 12) % 12;
  const octave = Math.floor(midi / 12) - 1;
  const name = NOTE_NAMES[note] ?? "C";
  if (name.length === 1) {
    return `${name}-${octave}`;
  }
  return `${name}${octave}`;
}

export function parseVolumeToken(token: string): number | undefined {
  const raw = token.trim();
  if (!raw || raw === ".." || raw === "." || raw === "--") {
    return undefined;
  }
  if (/^[0-9A-Fa-f]{1,2}$/.test(raw)) {
    return Math.min(1, Math.max(0, Number.parseInt(raw, 16) / 255));
  }
  return undefined;
}

export function formatVolume(volume: number | undefined): string {
  if (volume === undefined) {
    return "..";
  }
  return Math.round(volume * 255).toString(16).toUpperCase().padStart(2, "0");
}
